import type React from 'react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useNavigate, useOutletContext, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import type { PricingFormMode } from '@shared/dream-components';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type {
  PaymentIntentResult,
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  SetupIntentResult,
} from '@stripe/stripe-js';

import { useUpgradeContext } from '~/context/useUpgradeContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useCreateStripePriceSession from '~/hooks/useCreateStripePriceSession';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { OutletContext } from '~/interfaces/outlet_context';
import PremiumMemberModal from '~/pages/upgrade/components/PremiumMemberModal';
import useApplePay from '~/pages/upgrade/hooks/useApplePay';
import useFormState from '~/pages/upgrade/hooks/useFormState';
import useGiftCheckoutSubmitHandler from '~/pages/upgrade/hooks/useGiftCheckoutSubmitHandler';
import useGroupCheckoutSubmitHandler from '~/pages/upgrade/hooks/useGroupCheckoutSubmitHandler';
import useInternalCheckout from '~/pages/upgrade/hooks/useInternalCheckout';
import { getSSORedirectUrl } from '~/pages/upgrade/utils/getSSORedirectUrl';
import type { RootLoaderData } from '~/root';
import { buildGroupPurchaseCompleteUrl, GROUP_PURCHASE_COMPLETE_TOAST_KEYS } from '~/utils/groupPurchaseComplete';
import validateTaxId from '~/utils/validateTaxId';

import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import EmailConfirmationModal from './EmailConfirmationModal';
import type { UpgradeActionFetcherData } from './types';

type RenderProps = {
  isSubmitting: boolean;
  paymentRequest?: PaymentRequest;
  setEmail: (email: string) => void;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  canShowPaymentRequestButton: boolean;
  signupFlowId?: string;
  isCollectingBillingAddress?: boolean;
  onSuccess?: (redirectUrl?: string) => void;
};

const formatter = (currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
  });

const UpgradeAction = ({
  children,
  canShowPaymentRequestButton,
  signupFlowId,
  isCollectingBillingAddress,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const premiumOfferIdParam = searchParams.get('offer_id') || undefined;
  const fetcher = useComponentFetcher<any>();
  const isLoggedIn = useIsLoggedIn();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing3DSecure, setIsProcessing3DSecure] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [pendingSessionUrl, setPendingSessionUrl] = useState<string | null>(null);
  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] = useState(false);
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);

  const elements = useElements();
  const stripe = useStripe();
  const {
    stripeSession,
    selectedTier,
    selectedPrice,
    selectedGiftPrice,
    selectedGroupPrice,
    isInternalCheckout,
    email,
    setEmail,
    taxId,
    donationAmountCents,
    setDonationAmountCents,
    setTaxId,
    currentFormMode,
    seatCount,
    groupName,
    canLoginWithSSO,
  } = useUpgradeContext();
  const { setCookie, removeCookie, cookies, visitToken, referralId } = useOutletContext<OutletContext>();
  const rootData = useRouteLoaderData('root') as RootLoaderData;
  const subscriberId = rootData?.subscription?.subscriber_id;
  const usePaymentElement = rootData?.publication?.uses_stripe_payment_element;
  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);
  const { handleSubmit, handleSubmitWithPaymentElement } = useInternalCheckout();
  const handleClickGiftFormSubmit = useGiftCheckoutSubmitHandler({
    fetcher,
    setIsSubmitting,
    usePaymentElement,
    onSuccess: (redirectToSSOLogin) => handleSuccess(redirectToSSOLogin),
  });
  const handleClickGroupFormSubmit = useGroupCheckoutSubmitHandler({
    fetcher,
    setIsSubmitting,
    usePaymentElement,
    onSuccess: (redirectToSSOLogin) => handleSuccess(redirectToSSOLogin),
  });
  const { mutateAsync: createStripePriceSession } = useCreateStripePriceSession();
  const { nextPage, nextStep } = useSignupRedirects();
  const { handleOpenModal } = useGlobalModals();

  const { data: fetcherData, isSuccessful, isFailed } = useFormState<UpgradeActionFetcherData>(fetcher);

  const toastMessage = fetcherData?.toast?.message || '';

  const { paymentRequest, onPaymentRequestChange, applePayEvent, setApplePayEvent } = useApplePay({
    shouldCreatePaymentRequest: canShowPaymentRequestButton,
  });

  const validatedTaxId = validateTaxId(taxId);

  const handleClickSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);

    if (!isInternalCheckout) {
      if (selectedTier?.id === 'free') {
        // When user clicks on cta in a free tier price block
        // redirect to next step in signup flow (if in progress)
        // or signup modal
        if (nextStep) {
          nextPage();
          return;
        }
        handleOpenModal('signup');
        setIsSubmitting(false);
      } else if (!selectedPrice?.premium_offer || selectedPrice?.premium_offer.card_required) {
        if (!selectedPrice || !stripe) {
          setIsSubmitting(false);
          return;
        }

        // If user is logged in, proceed directly without email confirmation
        if (isLoggedIn && email) {
          setPendingPriceId(selectedPrice.id);
          handleEmailConfirmed(email);
          return;
        }

        // Show email confirmation modal before proceeding
        setPendingPriceId(selectedPrice.id);
        setIsEmailConfirmationModalOpen(true);
        return;
      } else {
        // When user clicks on cta in a price block
        // but due to a premium offer we don't need them to
        // pay using a card
        searchParams.set('modal', 'signup');
        if (selectedPrice.premium_offer) {
          searchParams.set('premium_offer_id', selectedPrice.premium_offer.id);
        }
        if (signupFlowId) {
          searchParams.set('signup_flow_id', signupFlowId);
        }

        setSearchParams(searchParams);
        setIsSubmitting(false);
      }

      return;
    }

    if (selectedPrice?.interval === 'donation') {
      if (!donationAmountCents || Number.isNaN(donationAmountCents)) {
        toast.error('Donation amount must be a number');
        setIsSubmitting(false);
        return;
      }

      if (donationAmountCents < 100) {
        toast.error(`Amount must be at least ${formatter(selectedPrice.currency).format(1)}`);
        setIsSubmitting(false);
        return;
      }

      if (donationAmountCents > 100000) {
        toast.error(`Amount must be at most ${formatter(selectedPrice.currency).format(1000)}`);
        setIsSubmitting(false);
        return;
      }
    }

    if (!email) {
      toast.error('Email is required');
      setIsSubmitting(false);
      return;
    }

    if (taxId && !validatedTaxId.isValid) {
      toast.error('Invalid tax ID');
      setIsSubmitting(false);
      return;
    }

    if (!stripeSession || !elements) {
      // Stripe.js hasn't yet loaded.
      toast.error('Something went wrong loading Stripe, please refresh the page and try again');
      setIsSubmitting(false);
      return;
    }

    if (!selectedPrice) {
      toast.error(t('upgrade.error.select_price'));
      setIsSubmitting(false);
      return;
    }

    try {
      if (usePaymentElement) {
        const response = await handleSubmitWithPaymentElement({
          event,
          stripeSession,
          elements,
          email,
          defaultErrorMessage: t('errors.default'),
        });

        handleSuccess(response?.redirectToSSOLogin ?? false);
      } else {
        await handleSubmit(event, fetcher, stripeSession, elements, email);
      }
    } catch (error: any) {
      toast.error(error.message || 'Something went wrong while creating a payment request, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeApplePayEvent = (status: 'success' | 'fail') => {
    if (applePayEvent) {
      applePayEvent.complete(status);
      setApplePayEvent(null);
    }
  };

  const successMessage = useMemo<string>(() => {
    const messagesByMode: Record<PricingFormMode, string> = {
      individual: t('upgrade.success.message'),
      gift: t('upgrade.tabs.gift.success.message'),
      group: t('upgrade.tabs.group.success.message'),
    };
    return messagesByMode[currentFormMode];
  }, [t, currentFormMode]);

  const handlersByMode: Record<PricingFormMode, (event: React.FormEvent<HTMLFormElement>) => Promise<void>> = {
    individual: handleClickSubmit,
    gift: handleClickGiftFormSubmit,
    group: handleClickGroupFormSubmit,
  };
  const onSubmit = handlersByMode[currentFormMode];

  const handleSuccess = async (redirectToSSOLogin: boolean) => {
    setIsSubmitting(false);
    setDonationAmountCents(0);
    setTaxId('');
    removeApplePayEvent('success');
    setHasRedirected(true);
    setCookie({ name: ACTIVE_UPGRADE_COOKIE_NAME, value: 'true', days: 4 });
    setIsProcessing3DSecure(false);

    // Group purchases land on the subscriber portal's Groups tab so the
    // purchaser is prompted to invite members next. Mirrors
    // `/group_purchase_complete` (the external Stripe Checkout success_url).
    if (currentFormMode === 'group') {
      const groupSubscriberId = fetcherData?.purchaserSubscriberId ?? subscriberId;
      if (groupSubscriberId) {
        await handleGroupSuccess(groupSubscriberId);
        return;
      }
    }

    // Create SSO redirect url if;
    // 1. The user is not logged in
    // 2. Not in gift mode
    // If creation fails, capture error and silently fallback to default behavior
    let ssoRedirectUrl: string | undefined;
    try {
      if (canLoginWithSSO && redirectToSSOLogin && rootData?.publication?.id && email) {
        const { redirect_url } = await getSSORedirectUrl({
          publication_id: rootData?.publication?.id,
          email,
          redirect_to: '/',
        });
        ssoRedirectUrl = redirect_url;
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    toast.success(toastMessage || successMessage);
    onSuccess?.(ssoRedirectUrl);
  };

  const handleGroupSuccess = async (groupSubscriberId: string) => {
    if (isLoggedIn) {
      navigate(buildGroupPurchaseCompleteUrl(groupSubscriberId, true, t));
      return;
    }

    // Mirror `/group_purchase_complete`: bounce through SSO so the toast
    // params survive the login hop and fire on the management page.
    const publicationId = rootData?.publication?.id;
    const hasSubscriptionPassword = rootData?.publication?.has_subscription_password;
    if (!hasSubscriptionPassword && publicationId && email) {
      try {
        const { redirect_url } = await getSSORedirectUrl({
          publication_id: publicationId,
          email,
          // The landing toast fires after the SSO hop, by which point the
          // purchaser is authenticated — prompt the next action ("invite
          // members") rather than telling them to log in (they just did).
          redirect_to: buildGroupPurchaseCompleteUrl(groupSubscriberId, true, t),
        });
        // Confirm payment before the login redirect. Without this the
        // purchaser is bounced to a login screen with no signal the payment
        // succeeded. The redirect is a client-side nav to `/authenticate`,
        // so this toast survives onto the login page.
        toast.success(t(GROUP_PURCHASE_COMPLETE_TOAST_KEYS.loggedOut));
        navigate(redirect_url);
        return;
      } catch (error) {
        Sentry.captureException(error);
      }
    }

    // Fallback: no session and no SSO — leave the user where they are
    // and surface the next step via toast.
    toast.success(t(GROUP_PURCHASE_COMPLETE_TOAST_KEYS.loggedOut));
    onSuccess?.();
  };

  const handleFail = (errorMessage?: string) => {
    removeApplePayEvent('fail');
    removeCookie(ACTIVE_UPGRADE_COOKIE_NAME);
    setIsProcessing3DSecure(false);
    setIsSubmitting(false);
    toast.error(errorMessage || toastMessage || t('errors.default'));
  };

  const clientSecret = fetcherData?.clientSecret;
  const action = fetcherData?.action;

  const handleConfirmPayment = async (clientSecret: string) => {
    setIsProcessing3DSecure(true);

    const onConfirmComplete = (response?: SetupIntentResult | PaymentIntentResult) => {
      const setupIntentResponse = response as SetupIntentResult;
      const paymentIntentResponse = response as PaymentIntentResult;

      if (
        setupIntentResponse?.setupIntent?.status === 'succeeded' ||
        paymentIntentResponse?.paymentIntent?.status === 'succeeded'
      ) {
        handleSuccess(fetcherData?.redirectToSSOLogin ?? false);
        return;
      }

      if (setupIntentResponse?.error?.message || paymentIntentResponse?.error?.message) {
        handleFail(setupIntentResponse?.error?.message || paymentIntentResponse?.error?.message);
        return;
      }

      handleFail();
    };

    try {
      let response: any;
      if (action === 'requires_confirmation') {
        const cardElement = elements?.getElement(CardElement);
        if (cardElement) {
          response = await stripe?.confirmCardSetup(clientSecret, {
            payment_method: { card: cardElement },
          });
        }
      } else if (action === 'requires_action') {
        const cardElement = elements?.getElement(CardElement);
        if (cardElement) {
          response = await stripe?.confirmCardPayment(clientSecret, {
            payment_method: { card: cardElement },
          });
        }
      } else if (applePayEvent) {
        response = await stripe?.confirmCardPayment(clientSecret, {
          payment_method: applePayEvent.paymentMethod.id,
        });
      }

      onConfirmComplete(response);
    } catch (error) {
      Sentry.captureException(error);
      handleFail();
    }
  };

  // On Attempts that where additional authentication or actions by the user are required
  useEffect(() => {
    if (clientSecret && action) {
      handleConfirmPayment(clientSecret);
    }
  }, [clientSecret, action]);

  // On successful or failed attempts
  useEffect(() => {
    if (isSuccessful && !hasRedirected) {
      handleSuccess(fetcherData?.redirectToSSOLogin ?? false);
    } else if (isFailed) {
      handleFail();
    }
  }, [isSuccessful, isFailed, hasRedirected]);

  // Handles changes in payment request (Apple Pay)
  const handleDuplicateContinue = async () => {
    if (pendingSessionUrl) {
      window.location.href = pendingSessionUrl;
    }
  };

  const handleEmailConfirmed = async (confirmedEmail: string) => {
    setIsEmailConfirmationModalOpen(false);
    setIsSubmitting(true);
    setEmail(confirmedEmail);

    if (!pendingPriceId) {
      setIsSubmitting(false);
      return;
    }

    try {
      const sessionResponse = await createStripePriceSession(
        pendingPriceId,
        confirmedEmail,
        premiumOfferIdParam || selectedPrice?.premium_offer?.id,
        isCollectingBillingAddress,
      );

      if (sessionResponse) {
        // Check if this is a duplicate subscription
        if (sessionResponse.is_duplicate) {
          setPendingSessionUrl(sessionResponse.url);
          setIsDuplicateModalOpen(true);
          setIsSubmitting(false);
          return;
        }

        // Not a duplicate, proceed normally
        if (sessionResponse.url) {
          window.location.href = sessionResponse.url;
        } else {
          toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
          setIsSubmitting(false);
        }
      } else {
        toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
        setIsSubmitting(false);
      }
    } catch {
      toast.error('Something went wrong while creating a Stripe session, please refresh the page and try again');
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest?.on('paymentmethod', (e: PaymentRequestPaymentMethodEvent) => {
        if (formRef.current) {
          onPaymentRequestChange(e, fetcher, formRef.current);
        }
      });
    }
  }, [paymentRequest, onPaymentRequestChange, fetcher]);

  return (
    <>
      <ActionForm formRef={formRef} fetcher={fetcher} method="post" action="/upgrade" onSubmit={onSubmit}>
        <StatusInputs name="upgrade" errorMessage={t('errors.default')} successMessage={successMessage} />
        {currentFormMode === 'gift' && <input type="hidden" value="true" name="is_gift" />}
        <input name="sent_from_orchid" type="hidden" defaultValue="true" />
        {referralId && <input hidden name="ref" defaultValue={referralId} />}
        {boostAffiliateRecommendingPublicationId && (
          <input hidden name="bhba" defaultValue={boostAffiliateRecommendingPublicationId} />
        )}
        {visitToken && <input hidden name="visit_token" defaultValue={visitToken} />}
        {signupFlowId ? <input type="hidden" name="signup_flow_id" defaultValue={signupFlowId} /> : null}
        {isInternalCheckout ? (
          <>
            <input type="hidden" name="force_three_d_secure" defaultValue={'false'} />
            {validatedTaxId.isValid ? <input type="hidden" name="tax_id_type" value={validatedTaxId.type} /> : null}
          </>
        ) : null}
        {cookies.last_resource_guid && (
          <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid} />
        )}
        {!isInternalCheckout && email && <input type="hidden" name="email" defaultValue={email} />}
        {currentFormMode === 'individual' && selectedPrice ? (
          <>
            <input type="hidden" name="price_id" defaultValue={selectedPrice.id} />
            {selectedPrice.premium_offer && (
              <input type="hidden" name="premium_offer_id" defaultValue={selectedPrice.premium_offer.id} />
            )}
            {selectedPrice?.interval === 'donation' && (
              <input type="hidden" name="amount_cents" defaultValue={donationAmountCents} />
            )}
          </>
        ) : null}
        {currentFormMode === 'gift' && selectedGiftPrice ? (
          <>
            <input type="hidden" name="price_id" defaultValue={selectedGiftPrice.id} />
            {selectedGiftPrice.premium_offer && (
              <input type="hidden" name="premium_offer_id" defaultValue={selectedGiftPrice.premium_offer.id} />
            )}
            {selectedGiftPrice?.interval === 'donation' && (
              <input type="hidden" name="amount_cents" defaultValue={donationAmountCents} />
            )}
          </>
        ) : null}
        {currentFormMode === 'group' && selectedGroupPrice ? (
          <>
            <input type="hidden" value="true" name="is_group" />
            <input type="hidden" name="price_id" defaultValue={selectedGroupPrice.id} />
            <input type="hidden" name="group_purchase_quantity" value={seatCount ?? ''} />
            <input type="hidden" name="group_name" value={groupName} />
            {selectedGroupPrice.premium_offer && (
              <input type="hidden" name="premium_offer_id" defaultValue={selectedGroupPrice.premium_offer.id} />
            )}
          </>
        ) : null}
        {children({
          isSubmitting: isSubmitting || isProcessing3DSecure || fetcher.state === 'submitting',
          paymentRequest,
          setEmail,
        })}
      </ActionForm>
      <PremiumMemberModal
        isUpgradeActive={isDuplicateModalOpen}
        onContinue={handleDuplicateContinue}
        onClose={() => setIsDuplicateModalOpen(false)}
        subscriberId={subscriberId}
      />
      <EmailConfirmationModal
        isOpen={isEmailConfirmationModalOpen}
        onClose={() => {
          setIsEmailConfirmationModalOpen(false);
          setIsSubmitting(false);
        }}
        onConfirm={handleEmailConfirmed}
        initialEmail={email}
      />
    </>
  );
};

export default UpgradeAction;
